/**
 * Yeast and bacteria cultures.
 *
 * Temperatures are °C (PLAN §5 canon); labs that publish °F are converted and
 * rounded to the nearest whole degree, which is why a 68–72 °F range shows as
 * 20–22 °C.
 */

import { z } from 'zod';
import {
  descriptionSchema,
  productUrlSchema,
  provenanceSchema,
  range,
  slugSchema,
  tagsSchema,
} from './common';

/** How the culture is packaged — drives pitch-rate and rehydration advice. */
export const yeastFormSchema = z.enum(['dry', 'liquid']);

/** @see yeastFormSchema */
export type YeastForm = z.infer<typeof yeastFormSchema>;

/**
 * Broad fermentation character/temperature family.
 *
 * `hybrid` covers kölsch/alt/steam strains that ferment cool but stay top
 * cropping; `wild` covers Brettanomyces and mixed cultures; `kveik` is called
 * out separately because its temperature envelope breaks every ale default.
 */
export const yeastTypeSchema = z.enum(['ale', 'lager', 'hybrid', 'wild', 'kveik']);

/** @see yeastTypeSchema */
export type YeastType = z.infer<typeof yeastTypeSchema>;

/** Settling behaviour, on the labs' shared five-step vocabulary. */
export const flocculationSchema = z.enum(['low', 'medium-low', 'medium', 'medium-high', 'high']);

/** @see flocculationSchema */
export type Flocculation = z.infer<typeof flocculationSchema>;

/** One yeast product. */
export const yeastSchema = z.strictObject({
  /** Stable slug including the lab, e.g. `fermentis-us-05`, `wyeast-1968`. */
  id: slugSchema,
  /** Lab or brand: Fermentis, Lallemand, Wyeast, White Labs, … */
  lab: z.string().min(2).max(40),
  /** The lab's catalogue code — `US-05`, `WLP001`, `1056`, `OYL-052`. */
  productCode: z.string().min(1).max(24),
  /** Marketing name, or the code again where the lab uses no separate name. */
  name: z.string().min(2).max(80),
  /** @see yeastFormSchema */
  form: yeastFormSchema,
  /** @see yeastTypeSchema */
  type: yeastTypeSchema,
  /**
   * Apparent attenuation, %.
   *
   * The floor is 5, not the 30 a brewing yeast never goes below, because the
   * catalog carries cultures that are *sold* for not attenuating: the
   * maltose-negative non-alcoholic strains (White Labs WLP618, WLP686) and the
   * nectar yeast WLP4650, published at 20-25 %.
   */
  attenuationPercent: range(5, 105),
  /** Recommended fermentation temperature, °C. */
  temperatureC: range(0, 45),
  /** @see flocculationSchema */
  flocculation: flocculationSchema,
  /**
   * Alcohol tolerance as % ABV.
   *
   * The point at which the lab stops guaranteeing the strain, not a hard wall.
   * `null` where the lab publishes only a word ("high") and we would otherwise
   * be inventing a number.
   */
  alcoholTolerancePercent: z.number().min(3).max(25).nullable(),
  /**
   * STA1 (`Saccharomyces` glucoamylase) status.
   *
   * `true` — the strain is a known STA1+ diastatic culture (will keep
   * attenuating and can over-carbonate in package). `false` — tested negative
   * by the lab. `null` — not published; treat as unknown, not as safe.
   */
  sta1: z.boolean().nullable(),
  /** Our own words. */
  description: descriptionSchema,
  /** Search tags: styles it suits, aliases, flavour notes. */
  tags: tagsSchema,
  /**
   * The maker's recommended dosage, **grams per hectolitre**, for dry yeast.
   *
   * The figure a dry-yeast maker actually publishes, and the honest basis for
   * "is this enough yeast?" on a dry pitch. A cell-count target is the wrong
   * instrument here: the 1.5 M cells/mL/°P lager rate comes from liquid-yeast
   * practice, and dry yeast ships with the glycogen and sterol reserves liquid
   * yeast has to build in the wort — which is why Fermentis can recommend what
   * looks like a heavy under-pitch on that scale and still ferment clean.
   *
   * `undefined` on liquid strains, where it means nothing, and on dry strains
   * whose maker publishes no figure — `core`'s `pitchPlan` falls back to a
   * class-typical band by yeast type there.
   */
  dosageGramsPerHl: range(0, 500).optional(),
  /** @see provenanceSchema */
  /** @see productUrlSchema */
  url: productUrlSchema.optional(),
  provenance: provenanceSchema,
});

/** @see yeastSchema */
export type Yeast = z.infer<typeof yeastSchema>;
